<template>
    <div ref="mapContainer" class="os-map-view" :style="{ height: height, width: '100%' }" />
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

interface MarkerPosition {
    lat: number
    lng: number
}

interface MarkerData {
    position: MarkerPosition
    icon?: string
    popup?: string
    id?: string
}

const props = defineProps({
    center: {
        type: Object as PropType<MarkerPosition>,
        default: () => ({ lat: 48.7, lng: 19.5 }) // Slovakia center
    },
    zoom: {
        type: Number,
        default: 7
    },
    markers: {
        type: Array as PropType<MarkerData[]>,
        default: () => []
    },
    height: {
        type: String,
        default: '400px'
    },
    markerIcon: {
        type: String,
        default: ''
    }
})

const emit = defineEmits<{
    markerClick: [marker: MarkerData, position: MarkerPosition]
    mapClick: [position: MarkerPosition]
}>()

const mapContainer = ref<HTMLElement | null>(null)
const map = ref<any>(null)
const leafletMarkers = ref<any[]>([])
const L = ref<any>(null)

// Load Leaflet from CDN
async function loadLeaflet(): Promise<any> {
    if (typeof window === 'undefined') return null

    // Check if already loaded
    if ((window as any).L) {
        return (window as any).L
    }

    // Load CSS
    if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
        link.crossOrigin = ''
        document.head.appendChild(link)
    }

    // Load JS
    return new Promise((resolve, reject) => {
        if ((window as any).L) {
            resolve((window as any).L)
            return
        }

        const script = document.createElement('script')
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
        script.crossOrigin = ''
        script.onload = () => {
            resolve((window as any).L)
        }
        script.onerror = reject
        document.head.appendChild(script)
    })
}

// Initialize the map
async function initMap() {
    if (!mapContainer.value) return

    try {
        L.value = await loadLeaflet()
        if (!L.value) return

        // Create map
        map.value = L.value.map(mapContainer.value).setView(
            [props.center.lat, props.center.lng],
            props.zoom
        )

        // Add OpenStreetMap tiles
        L.value.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19
        }).addTo(map.value)

        // Add click handler
        map.value.on('click', (e: any) => {
            emit('mapClick', { lat: e.latlng.lat, lng: e.latlng.lng })
        })

        // Add initial markers
        updateMarkers()
    } catch (error) {
        console.error('Failed to initialize map:', error)
    }
}

// Create custom icon
function createIcon(iconUrl?: string) {
    if (!L.value) return null

    if (iconUrl) {
        return L.value.icon({
            iconUrl,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            popupAnchor: [0, -40]
        })
    }

    // Default marker icon
    return L.value.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
    })
}

// Update markers on the map
function updateMarkers() {
    if (!map.value || !L.value) return

    // Remove existing markers
    leafletMarkers.value.forEach((marker) => {
        map.value.removeLayer(marker)
    })
    leafletMarkers.value = []

    // Add new markers
    props.markers.forEach((markerData) => {
        if (markerData.position.lat && markerData.position.lng) {
            const icon = createIcon(markerData.icon || props.markerIcon)
            const marker = L.value.marker(
                [markerData.position.lat, markerData.position.lng],
                icon ? { icon } : {}
            ).addTo(map.value)

            // Add popup if provided
            if (markerData.popup) {
                marker.bindPopup(markerData.popup)
            }

            // Add click handler
            marker.on('click', () => {
                emit('markerClick', markerData, markerData.position)
            })

            leafletMarkers.value.push(marker)
        }
    })
}

// Update map center
function setCenter(center: MarkerPosition) {
    if (map.value) {
        map.value.setView([center.lat, center.lng], map.value.getZoom())
    }
}

// Update zoom level
function setZoom(zoom: number) {
    if (map.value) {
        map.value.setZoom(zoom)
    }
}

// Fit bounds to show all markers
function fitBounds() {
    if (!map.value || !L.value || leafletMarkers.value.length === 0) return

    const group = L.value.featureGroup(leafletMarkers.value)
    map.value.fitBounds(group.getBounds().pad(0.1))
}

// Expose methods
defineExpose({
    setCenter,
    setZoom,
    fitBounds,
    updateMarkers
})

// Watch for changes
watch(() => props.center, (newCenter) => {
    setCenter(newCenter)
}, { deep: true })

watch(() => props.zoom, (newZoom) => {
    setZoom(newZoom)
})

watch(() => props.markers, () => {
    updateMarkers()
}, { deep: true })

// Lifecycle
onMounted(() => {
    initMap()
})

onBeforeUnmount(() => {
    if (map.value) {
        map.value.remove()
        map.value = null
    }
})
</script>

<style>
.os-map-view {
    z-index: 0;
}

/* Fix for Leaflet icons in Nuxt */
.leaflet-default-icon-path {
    background-image: url('https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png');
}

/* Map tiles retain their original geography colors; controls and popups follow the theme. */
[data-theme="dark"] .os-map-view .leaflet-control a,
[data-theme="dark"] .os-map-view .leaflet-popup-content-wrapper,
[data-theme="dark"] .os-map-view .leaflet-popup-tip,
[data-theme="dark"] .os-map-view .leaflet-control-attribution {
    background: var(--color-surface-card);
    color: var(--color-txt-body);
}
</style>
